import React, { useState, useEffect } from 'react';
import './Contact.css';

import { Heading, PageTitle } from '../../components';
import { contactContent } from '../../constants';

import { FaComment, FaEnvelope, FaPhoneAlt, FaUserAlt } from 'react-icons/fa';
import axios from "axios";

// API path for sending form data
const API_PATH = 'https://www.dworimielno.com/api/phpmailer/submit.php';

// Regular expressions for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const Contact = () => {

  // State to manage form input values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    message: ''
  });
  
  // State for handling success and error messages
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect to clear success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Function to handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setIsSubmitting(true);
    setError('');

    try {

      // Client-side validation
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Imie i nazwisko jest wymagane');
      if (!formData.email.trim()) {
        validationErrors.push('Adres email jest wymagany');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Błędny format adresu email');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Numer telefony jest wymagany');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Błędny format numeru telefonu');
      }
      if (!formData.message.trim()) validationErrors.push('Wymagana jest treść wiadomości');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | ')); // Concatenate errors with '|'
      }

      // Prepare form data for API request
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Sending form data to API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Błąd wysłania wiadomości');
      }

      // On success, reset form and show success message
      setMailSent(true);
      setFormData({ name: '', email: '', number: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Pojawił się problem');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle input changes and update state
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      {/* Page Title */}
          <PageTitle title={'Kontakt'} page={'Kontakt'} heading={'Ogród Dwór Imielno'} subTitle={'Centrum kultury Obszarów wiejskich i edukacji ekologicznej oraz zachowania różnorodności biologicznej'} />
    
          <section className='contact' id='contact'>
        
        {/* Contact Information Section */}
        <div className='contact-info'>
          {contactContent.map((contact) => (
            <div className='info-item' key={contact.id}>
              <div className='icon'>{contact.icon}</div>
              <div className="content">
                <h3>{contact.title}</h3>
                {contact.content.map((value, index) => (
                  <p key={index}>{value}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className='box-container'>
          
          {/* Embedded Google Map */}
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2438.5726906292775!2d19.21892617623305!3d52.32375597201041!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471b77f2c1d0c527%3A0xca494e9f453f96d3!2sFundacja%20Dw%C3%B3r%20Imielno%20-%20Centrum%20Kultury%20Obszar%C3%B3w%20Wiejskich%2C%20Edukacji%20Ekologicznej%20i%20Zachowania%20R%C3%B3%C5%BCnorodno%C5%9Bci%20Biologicznej!5e0!3m2!1spl!2spl!4v1775915351663!5m2!1spl!2spl" 
          allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
          

          {/* Contact Form */}
          <form className='dark-form' onSubmit={handleSubmitForm}>

            <Heading title={'Wyślij nam wiadomość'} subTitle={'Skontaktuj się z nami'} sideHeading linear />

            {/* Name Input Field */}
            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="name"><FaUserAlt /></label>
                <input
                  type="text"
                  className='box'
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Imię i nazwisko"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            {/* Email Input Field */}
            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  className='box'
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>

            {/* Phone Number Input Field */}
            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="number"><FaPhoneAlt /></label>
                <input
                  type="tel"
                  className='box'
                  id="number"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                  placeholder="Numer telefonu"
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>

            {/* Message Input Field */}
            <div className='input-box'>
              <div className='input-field text-area'>
                <label htmlFor="message"><FaComment /></label>
                <textarea
                  cols="30"
                  rows="10"
                  id="message"
                  className='box'
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Wiadomość"
                  disabled={isSubmitting}
                  required 
                ></textarea>
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? 'Wysyłanie...' : 'Wyślij'}
            </button>

            {/* Error and Success Messages */}
            {error && <div className="alert error">{error.split(' | ').map((msg, i) => (<span key={i}>{msg}</span>))}</div>}
            {mailSent && <div className="alert success"><span>Wiadomość wysłana</span></div>}
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;